/**
 * Goal seek: work backwards from the result you want (decisions 6.11-6.13, docs/goal-seek.md).
 *
 *   1. target     sliders, measured live against the result on screen. Targets, never predictions.
 *   2. ceilings   tick the concerns an edit could address; the ceiling is a union of personas who
 *                 already posted, so it is arithmetic - and it says when a target is out of reach.
 *   3. suggest    candidate edits, each checked against the locked facts before it costs anything.
 *   4. triage     one quick look at every candidate, clearly indicative.
 *   5. test       the real thing, on the main community and an independent holdout.
 */

import { CheckCircle2, FlaskConical, Play, Target, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { isFinished } from "@/api/jobs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  type CandidateView,
  type GoalSeek,
  type Objective,
  type Triage,
  useAdopt,
  useCandidateJob,
  useGoalSeek,
  useGoalSeekPrices,
  useJobs,
  useSetObjective,
  useSuggest,
} from "@/lib/queries";

import { DraggablePanel } from "./DraggablePanel";

const PERCENT_FIELDS = new Set(["min_positive_share", "max_negative_share", "max_backlash_share"]);
const CONSTRAINT_LABELS: Record<string, string> = {
  min_positive_share: "Positive share at least",
  max_negative_share: "Negative share at most",
  max_backlash_share: "Backlash at most",
  min_mean_stance: "Mean stance at least",
  max_polarisation: "Polarisation at most",
};
const RANGES: Record<string, { min: number; max: number; step: number; start: number }> = {
  min_positive_share: { min: 0, max: 1, step: 0.05, start: 0.5 },
  max_negative_share: { min: 0, max: 1, step: 0.05, start: 0.3 },
  max_backlash_share: { min: 0, max: 1, step: 0.05, start: 0.1 },
  min_mean_stance: { min: -2, max: 2, step: 0.1, start: 0.5 },
  max_polarisation: { min: 0, max: 2, step: 0.1, start: 1 },
};

export function GoalSeekPanel({ studyId, runId, onClose }: { studyId: string; runId: string; onClose: () => void }) {
  const state = useGoalSeek(studyId, runId);
  const jobs = useJobs(studyId).data ?? [];
  const busy = jobs.some((j) => !isFinished(j));
  const view = state.data;

  return (
    <DraggablePanel title="Goal seek" storageKey="goal-seek-panel" onClose={onClose} width={520}>
      {!view ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4 text-sm">
          {view.holdout_failure_streak >= 2 && (
            <Alert variant="destructive">
              <AlertDescription>
                {view.holdout_failure_streak} rounds in a row, gains on the main community did not hold up on the
                holdout. That usually means the message is being tuned to quirks of this simulation. Consider
                stopping, testing the best version with real people, or changing the target.
              </AlertDescription>
            </Alert>
          )}
          <TargetForm studyId={studyId} view={view} />
          <Ceilings view={view} />
          <Suggestions studyId={studyId} view={view} busy={busy} />
        </div>
      )}
    </DraggablePanel>
  );
}

function TargetForm({ studyId, view }: { studyId: string; view: GoalSeek }) {
  const save = useSetObjective(studyId);
  const [objective, setObjective] = useState<Objective>(view.objective);

  function update(change: Partial<Objective>) {
    const next = { ...objective, ...change };
    setObjective(next);
    save.mutate(next);
  }

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-1 font-medium">
        <Target className="size-4" aria-hidden="true" /> 1 · The target
      </p>
      <label className="block text-xs text-muted-foreground" htmlFor="goal-primary">
        Main goal
      </label>
      <select
        id="goal-primary"
        className="h-8 w-full rounded-md border border-white/10 bg-[#0f1115] px-2 text-xs"
        value={objective.primary ?? "maximise_positive_share"}
        onChange={(event) => update({ primary: event.target.value as Objective["primary"] })}
      >
        {Object.entries(view.goals).map(([id, label]) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </select>

      <div className="space-y-1.5">
        {Object.entries(CONSTRAINT_LABELS).map(([field, label]) => {
          const value = objective[field as keyof Objective] as number | null | undefined;
          const range = RANGES[field];
          const on = value !== null && value !== undefined;
          return (
            <div key={field} className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`goal-${field}`}
                checked={on}
                onChange={(event) => update({ [field]: event.target.checked ? range.start : null } as Partial<Objective>)}
              />
              <label htmlFor={`goal-${field}`} className="w-44 shrink-0 text-xs">
                {label}
              </label>
              <input
                type="range"
                aria-label={label}
                className="flex-1 accent-[#3987e5]"
                min={range.min}
                max={range.max}
                step={range.step}
                value={on ? value! : range.start}
                disabled={!on}
                onChange={(event) => update({ [field]: Number(event.target.value) } as Partial<Objective>)}
              />
              <span className="w-12 text-right text-xs tabular-nums">
                {on ? formatTarget(field, value!) : "–"}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        These are targets, not predictions: moving one changes what you are asking for, never a figure.
      </p>

      {view.standing ? (
        <table className="w-full text-xs">
          <tbody>
            <tr>
              <td className="py-0.5 text-muted-foreground">{view.standing.primary_label}</td>
              <td className="py-0.5 text-right tabular-nums">{view.standing.primary_text}</td>
              <td className="py-0.5 pl-2 text-muted-foreground">goal</td>
            </tr>
            {view.standing.constraints.map((constraint) => (
              <tr key={constraint.field}>
                <td className="py-0.5 text-muted-foreground">
                  {constraint.label} {constraint.comparison} {constraint.target_text}
                </td>
                <td className="py-0.5 text-right tabular-nums">{constraint.actual_text}</td>
                <td className={`py-0.5 pl-2 ${constraint.met ? "text-step-done" : "text-[#ff8f75]"}`}>
                  {constraint.met ? "met" : constraint.short_by_text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-muted-foreground">
          Run the working version to see where it stands against this target.
        </p>
      )}
    </section>
  );
}

function formatTarget(field: string, value: number): string {
  return PERCENT_FIELDS.has(field) ? `${Math.round(value * 100)}%` : value.toFixed(1);
}

/** Tick the concerns an edit could address; the ceiling is the union of the people behind them. */
function Ceilings({ view }: { view: GoalSeek }) {
  const [ticked, setTicked] = useState<string[]>([]);
  const target = view.objective.min_positive_share ?? null;

  const combined = useMemo(() => {
    if (!view.total_personas) return null;
    const people = new Set<string>();
    for (const concern of view.headroom) {
      if (ticked.includes(concern.theme_id)) concern.persona_ids.forEach((id) => people.add(id));
    }
    return { share: (view.positive_personas + people.size) / view.total_personas, people: people.size };
  }, [view, ticked]);

  if (view.headroom.length === 0) {
    return (
      <section className="space-y-1 border-t border-white/10 pt-3">
        <p className="font-medium">2 · What could move</p>
        <p className="text-xs text-muted-foreground">
          {view.themes_error
            ? `No concern themes for this run (${view.themes_error}), so there is no headroom to show.`
            : "No concern themes were found in this run."}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-2 border-t border-white/10 pt-3">
      <p className="font-medium">2 · What could move</p>
      <p className="text-xs text-muted-foreground">
        Tick the concerns you think an edit could address. Each ceiling is what the positive share would be if every
        non-positive persona who raised it were won over - an upper bound, never a forecast. Real edits convert fewer
        people and can lose others.
      </p>
      <ul className="space-y-1">
        {view.headroom.map((concern) => (
          <li key={concern.theme_id} className="flex items-start gap-2">
            <input
              type="checkbox"
              id={`concern-${concern.theme_id}`}
              className="mt-0.5"
              checked={ticked.includes(concern.theme_id)}
              onChange={(event) =>
                setTicked((current) =>
                  event.target.checked
                    ? [...current, concern.theme_id]
                    : current.filter((id) => id !== concern.theme_id),
                )
              }
            />
            <label htmlFor={`concern-${concern.theme_id}`} className="flex-1 text-xs">
              {concern.label}
              <span className="ml-1 text-muted-foreground">
                · {concern.personas} personas · ceiling {Math.round(concern.ceiling * 100)}%
              </span>
            </label>
          </li>
        ))}
      </ul>
      {combined && ticked.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-black/30 p-2 text-xs">
          <p>
            {ticked.length} ticked: ceiling{" "}
            <span className="text-foreground">{Math.round(combined.share * 100)}%</span>, from {combined.people} of{" "}
            {view.total_personas} personas who raised at least one.
          </p>
          {target !== null && (
            <p className={combined.share >= target ? "text-step-done" : "text-[#ff8f75]"}>
              {combined.share >= target
                ? `Your ${Math.round(target * 100)}% target sits inside that ceiling: an edit could reach it, at best.`
                : `Even if all of these were fully addressed, the ceiling is ${Math.round(combined.share * 100)}% against your ${Math.round(target * 100)}% target. This target is probably out of reach by editing alone - change the offer, the audience, or the target.`}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Suggestions({ studyId, view, busy }: { studyId: string; view: GoalSeek; busy: boolean }) {
  const batch = view.batches[0];
  const untested = (batch?.candidates ?? []).filter((c) => !c.tested_variant_id && c.blockers.length === 0);
  const prices = useGoalSeekPrices(studyId, Math.max(untested.length, 1));
  const suggest = useSuggest(studyId);
  const triage = useCandidateJob(studyId, "triage");
  const test = useCandidateJob(studyId, "test");
  const adopt = useAdopt(studyId);
  const jobs = useJobs(studyId).data ?? [];
  const [chosen, setChosen] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const triageJob = jobs.filter((j) => j.kind === "triage").at(-1);
  const looks = triageJob?.status === "done" ? ((triageJob.result as unknown as Triage)?.looks ?? []) : [];

  const selections = Object.fromEntries(
    untested.filter((c) => chosen.includes(c.candidate_id)).map((c) => [c.candidate_id, c.text]),
  );
  const needsAcknowledging = untested.filter(
    (c) => chosen.includes(c.candidate_id) && c.warnings.length > 0 && !acknowledged.includes(c.candidate_id),
  );

  return (
    <section className="space-y-2 border-t border-white/10 pt-3">
      <p className="font-medium">3 · Suggested edits</p>
      <p className="text-xs text-muted-foreground">
        Suggestions keep all {view.locked_facts} locked fact(s) word for word, never drop a caveat, and mark anything
        unevidenced rather than inventing it.
        {prices.data && !prices.data.mock && ` About $${prices.data.suggest_usd.toFixed(2)} of credit.`}
      </p>
      <Button size="sm" disabled={busy || suggest.isPending || !view.standing}
              onClick={() => suggest.mutate(undefined, {
                onError: (error: unknown) =>
                  toast.error(error instanceof Error ? error.message : "could not ask for suggestions"),
              })}>
        <Wand2 aria-hidden="true" /> {suggest.isPending ? "Asking…" : "Suggest edits"}
      </Button>

      {batch && (
        <div className="space-y-2">
          {batch.candidates.map((candidate) => (
            <Candidate
              key={candidate.candidate_id}
              candidate={candidate}
              look={looks.find((l) => l.candidate_id === candidate.candidate_id)}
              chosen={chosen.includes(candidate.candidate_id)}
              acknowledged={acknowledged.includes(candidate.candidate_id)}
              onChoose={(on) =>
                setChosen((current) =>
                  on ? [...current, candidate.candidate_id] : current.filter((id) => id !== candidate.candidate_id),
                )
              }
              onAcknowledge={(on) =>
                setAcknowledged((current) =>
                  on ? [...current, candidate.candidate_id] : current.filter((id) => id !== candidate.candidate_id),
                )
              }
            />
          ))}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy || untested.length === 0}
              onClick={() =>
                triage.mutate(
                  { batch_id: batch.batch_id, selections: Object.fromEntries(untested.map((c) => [c.candidate_id, c.text])) },
                  { onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "could not start") },
                )
              }
            >
              <FlaskConical aria-hidden="true" /> Quick look at all {untested.length}
            </Button>
            <Button
              size="sm"
              disabled={busy || Object.keys(selections).length === 0 || needsAcknowledging.length > 0}
              onClick={() =>
                test.mutate(
                  { batch_id: batch.batch_id, selections },
                  { onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "could not start") },
                )
              }
            >
              <Play aria-hidden="true" /> Test {Object.keys(selections).length} properly
            </Button>
          </div>
          {needsAcknowledging.length > 0 && (
            <p className="text-xs text-[#ff8f75]">
              Review the warnings on {needsAcknowledging.map((c) => c.title).join(", ")} before testing.
            </p>
          )}
          {untested.length === 0 && batch.candidates.every((c) => !c.tested_variant_id) && (
            <p className="text-xs text-[#ff8f75]">
              Every candidate was blocked by the automatic checks, so there is nothing to look at or test. Ask for
              suggestions again, or fix what is blocking one - usually an unevidenced claim or a changed figure.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            A quick look is 8 personas and one round: enough to drop the obvious losers, not to settle a close one.
            Testing runs each candidate on the whole audience and on an independent holdout community, so a gain has
            to hold up on people it was not tuned against.
            {prices.data && !prices.data.mock &&
              ` Quick look about $${prices.data.triage_usd.toFixed(2)}; testing about $${prices.data.test_usd.toFixed(2)}.`}
          </p>
        </div>
      )}

      {view.results.length > 0 && (
        <div className="space-y-1 border-t border-white/10 pt-2">
          <p className="font-medium">4 · What the tests found</p>
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th scope="col" className="text-left font-medium">Candidate</th>
                <th scope="col" className="text-right font-medium">Main</th>
                <th scope="col" className="text-right font-medium">Holdout</th>
                <th scope="col" className="text-right font-medium" />
              </tr>
            </thead>
            <tbody>
              {view.results.map((result) => (
                <tr key={result.candidate_id}>
                  <td className="py-0.5">{result.title}</td>
                  <td className="py-0.5 text-right">
                    {result.main_change_text} · {result.main_verdict}
                  </td>
                  <td className="py-0.5 text-right">
                    {result.holdout_change_text} · {result.holdout_verdict}
                  </td>
                  <td className="py-0.5 text-right">
                    <Button size="xs" variant="ghost" disabled={adopt.isPending}
                            onClick={() => adopt.mutate(result.variant_id, {
                              onSuccess: () => toast.success(`${result.title} is now the working version`),
                            })}>
                      <CheckCircle2 aria-hidden="true" /> Adopt
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Candidate({
  candidate,
  look,
  chosen,
  acknowledged,
  onChoose,
  onAcknowledge,
}: {
  candidate: CandidateView;
  look: Triage["looks"][number] | undefined;
  chosen: boolean;
  acknowledged: boolean;
  onChoose: (on: boolean) => void;
  onAcknowledge: (on: boolean) => void;
}) {
  const blocked = candidate.blockers.length > 0;
  return (
    <article className="rounded-lg border border-white/10 bg-black/20 p-2 text-xs">
      <div className="flex items-start gap-2">
        {!candidate.tested_variant_id && (
          <input
            type="checkbox"
            aria-label={`Test ${candidate.title}`}
            className="mt-0.5"
            checked={chosen}
            disabled={blocked}
            onChange={(event) => onChoose(event.target.checked)}
          />
        )}
        <div className="flex-1">
          <p className="font-medium">
            {candidate.title}{" "}
            <span className="font-normal text-muted-foreground">
              {candidate.tested_variant_id ? `· tested → ${candidate.tested_variant_id}` : "· untested: no effect is known yet"}
            </span>
          </p>
          <p className="mt-1 text-muted-foreground">{candidate.text}</p>
          {candidate.changes.map((change, index) => (
            <p key={index} className="mt-1 text-muted-foreground">
              · {String((change as { change?: string }).change ?? "")}
            </p>
          ))}
          {candidate.blockers.map((blocker) => (
            <p key={blocker} className="mt-1 text-[#ff8f75]">⛔ {blocker}</p>
          ))}
          {candidate.warnings.map((warning) => (
            <p key={warning} className="mt-1 text-step-next">⚠ {warning}</p>
          ))}
          {candidate.warnings.length > 0 && chosen && (
            <label className="mt-1 flex items-center gap-1">
              <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} />
              I have reviewed the warnings
            </label>
          )}
          {look && (
            <p className="mt-1 text-step-next">
              Quick look ({look.personas} personas, indicative): positive{" "}
              {Math.round((look.metrics.values.positive_share ?? 0) * 100)}%
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
